import { useEffect, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { isAndroidApp, playUpdateInfo, openPlayStore, UPDATE_STATE } from '../utils/appUpdate';

/**
 * ForceUpdateGuard — when a newer version is live on the Play Store, the APK
 * STOPS until it is updated. Ported from SharpAccounting, where it has run in
 * production; renders nothing on web.
 *
 * This is NOT the OTA path (`OtaUpdater.jsx`), and the two never fight: OTA
 * ships a web bundle for the SAME release and the plugin refuses anything at or
 * below the native version, while this guard is about a new APK/AAB on Play —
 * a new plugin, permission or native config, which OTA can never deliver.
 *
 * Detection is Google's OFFICIAL In-App Updates API, asked once per launch
 * through `utils/appUpdate.js` (the same answer the version card on My Profile
 * and the top bar's ⬆ marker read, so the three can never disagree). On start:
 *   1. the state is `latest` or `unknown` → let the app through.
 *   2. `update` → try Google's native IMMEDIATE flow (their own full-screen
 *      blocking updater).
 *   3. If that flow is not allowed, is cancelled or errors → our own
 *      non-dismissable block, whose only choices are **Update now** (opens the
 *      Play Store) and **Exit** (closes the app). It prints the installed
 *      version and the Play Store's build number, so it is plain WHAT is old.
 *
 * **Fail-open.** In-app updates only answer for a build installed FROM Play
 * (internal testing counts). On a sideloaded `build-apk.ps1` APK, offline, or
 * on any API error the state is `unknown` and we DO NOT block — a transient
 * hiccup must never brick the app.
 */
export default function ForceUpdateGuard() {
  const [info, setInfo] = useState(null);   // set only when we are blocking

  useEffect(() => {
    if (!isAndroidApp()) return undefined;   // web / desktop — no gate
    let cancelled = false;
    let handle;

    const check = async (refresh) => {
      const res = await playUpdateInfo(refresh ? { refresh: true } : undefined);
      if (cancelled || res.state !== UPDATE_STATE.UPDATE) return;

      if (res.immediateUpdateAllowed) {
        try {
          const { AppUpdate } = await import('@capawesome/capacitor-app-update');
          await AppUpdate.performImmediateUpdate();
          // Reached only when the user backed out of Google's own flow — fall
          // through to our block below.
        } catch { /* immediate flow unavailable / failed → fall through */ }
      }
      if (!cancelled) setInfo(res);
    };

    check(false);

    // ...and again on every RESUME. A phone rarely cold-starts: the app sits in
    // the background for days, so a once-per-mount check could miss a release
    // for a week. `refresh: true` goes past the cached answer, the same way
    // OtaUpdater re-asks for a new bundle on resume.
    CapApp.addListener('appStateChange', ({ isActive }) => { if (isActive) check(true); })
      .then((h) => { handle = h; })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (handle) { try { handle.remove(); } catch { /* noop */ } }
    };
  }, []);

  // While the block is up the hardware Back button closes the app — the only
  // way past this screen is the Play Store. (BackButtonHandler's own listener
  // may still run; the overlay covers the page, and exitApp wins.)
  useEffect(() => {
    if (!info || !isAndroidApp()) return undefined;
    let handle;
    CapApp.addListener('backButton', () => { CapApp.exitApp(); })
      .then((h) => { handle = h; })
      .catch(() => {});
    return () => { if (handle) { try { handle.remove(); } catch { /* noop */ } } };
  }, [info]);

  if (!info) return null;

  return (
    <div className="force-update" role="alertdialog" aria-modal="true">
      <div className="force-update-card">
        <div className="force-update-head">⬆️ New update available</div>
        <p>
          A newer version of <b>Mehfil</b> is on the Play Store and is required
          to carry on.
        </p>
        <p className="force-update-sub">
          Please update from the Play Store to keep using the app.
        </p>
        {/* What is old, in numbers — the Play Store only tells Android the
            versionCode of the new build, never its version name. */}
        <div className="vc-rows force-update-ver">
          <div><span>Installed version</span><b>{info.installed}</b></div>
          {!!info.availableCode && (
            <div><span>Play Store build</span><b>{info.availableCode}{info.installedCode ? ` (you have ${info.installedCode})` : ''}</b></div>
          )}
        </div>
        <div className="force-update-actions">
          <button type="button" className="btn ghost" onClick={() => CapApp.exitApp()}>Exit</button>
          <button type="button" className="btn primary" onClick={openPlayStore}>Update now</button>
        </div>
      </div>
    </div>
  );
}
